import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../../services/TranslationService";
import CompanyService from "../../../../services/CompanyService";
import { ButtonTooltipIcon } from "../../../shared/components/ButtonTooltipIcon";
import AIBillingService, {
  AIBillingAlertSettings,
  AIBillingDashboardResponse,
  AIBillingDateRange,
  AIBillingErrorKind,
  AIBillingPeriodRequest,
  AIBillingUsageItem,
  getErrorKind
} from "../../../../services/AIBillingService";
import AIBillingKpiCard from "./components/AIBillingKpiCard";
import AIBillingPeriodSelector, {
  AIBillingPeriodOption,
  exceedsMaxRange,
  getPeriodRange
} from "./components/AIBillingPeriodSelector";
import AIBillingUsageTable from "./components/AIBillingUsageTable";
import AIBillingRechargeList from "./components/AIBillingRechargeList";
import AIBillingAlertBox from "./components/AIBillingAlertBox";
import AIBillingStateBlock, {
  AIBillingForbiddenBlock
} from "./components/AIBillingStateBlock";
import AIBillingStatusPill from "./components/AIBillingStatusPill";
import {
  formatAmountUsd,
  formatTokens,
  getAgentStatusLabel,
  getAgentStatusNote,
  getAgentStatusVariant,
  getErrorMessage,
  isAgentStatusBlocking
} from "./AIBillingFormat";
import { navigateToAIBillingTab } from "./AIBillingNavigation";
import { useUsageExport } from "./AIBillingExport";

const LATEST_USAGE_SIZE = 5;

/** MOCK — `/aibilling/dashboard` todavía no devuelve `alertSettings`
 * (ver docs/tickets/GDC-2997-consultas-backend.md, punto 4).
 * Reemplazar por `dashboard.alertSettings` cuando el backend lo entregue. */
const MOCK_ALERT_SETTINGS: AIBillingAlertSettings = {
  enabled: true,
  email: "admin@empresa.com",
  consumedPercentageThreshold: 80,
  lastNotifiedAt: null
};

const AIAgentUsage = () => {
  const { translate } = TranslationService;

  const [dashboard, setDashboard] = useState<AIBillingDashboardResponse | null>(
    null
  );
  const [latestUsage, setLatestUsage] = useState<AIBillingUsageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] =
    useState<AIBillingErrorKind>("none");
  const [usageError, setUsageError] = useState<AIBillingErrorKind>("none");
  const [periodOption, setPeriodOption] =
    useState<AIBillingPeriodOption>("current");
  const [customRange, setCustomRange] = useState<AIBillingDateRange>({});

  const range =
    periodOption === "custom" ? customRange : getPeriodRange(periodOption);
  const rangeIncomplete = !range.from || !range.to;
  const rangeTooLong = exceedsMaxRange(range);
  const period: AIBillingPeriodRequest | null =
    range.from && range.to ? { from: range.from, to: range.to } : null;

  const fetchAll = useCallback(async (dateRange: AIBillingDateRange) => {
    if (!dateRange.from || !dateRange.to) {
      return;
    }
    const period = { from: dateRange.from, to: dateRange.to };
    setLoading(true);
    setDashboardError("none");
    setUsageError("none");

    const [dashboardResult, usageResult] = await Promise.all([
      AIBillingService.getDashboard(period),
      AIBillingService.getUsage({
        ...period,
        page: 0,
        pageSize: LATEST_USAGE_SIZE
      })
    ]);

    setLoading(false);

    setDashboardError(getErrorKind(dashboardResult));
    setUsageError(getErrorKind(usageResult));

    setDashboard(dashboardResult instanceof Error ? null : dashboardResult);
    setLatestUsage(
      usageResult instanceof Error
        ? []
        : (usageResult.items ?? usageResult.list ?? [])
    );
  }, []);

  useEffect(() => {
    if (rangeIncomplete || rangeTooLong) {
      return;
    }
    fetchAll(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll, periodOption, customRange, rangeIncomplete, rangeTooLong]);

  const retry = useCallback(() => fetchAll(range), [fetchAll, range]);
  const { exporting, onExport } = useUsageExport(range);

  const getStatusAlert = (
    data: AIBillingDashboardResponse
  ): { variant: string; label: string; note: string } | null => {
    if (isAgentStatusBlocking(data.status)) {
      return {
        variant: getAgentStatusVariant(data.status),
        label: getAgentStatusLabel(data.status),
        note: getAgentStatusNote(data.status)
      };
    }
    if (!data.agentEnabled) {
      return {
        variant: "secondary",
        label: translate.AIUsageAgentDisabled,
        note: translate.AIUsageStatusDisabledNote
      };
    }
    return null;
  };

  const statusAlert = dashboard ? getStatusAlert(dashboard) : null;
  const showStatusAlert = statusAlert && dashboard.status !== "not_configured";

  return (
    <div style={{ minWidth: 0 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0" style={{ fontWeight: 500 }}>
          {translate.AIAgentUsageTitle}
        </h5>
        <button
          className="btn btn-secondary"
          onClick={retry}
          disabled={loading}
        >
          <i className={`far fa-sync-alt${loading ? " fa-spin" : ""}`} />
          {translate.Refresh}
        </button>
      </div>

      <AIBillingPeriodSelector
        value={periodOption}
        onChange={setPeriodOption}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
        note={translate.AIPeriodNote}
      />

      {rangeIncomplete ? (
        <div className="alert alert-info mb-0">
          {translate.AISelectDateRange}
        </div>
      ) : rangeTooLong ? (
        <div className="alert alert-warning mb-0">
          {translate.AIUsageRangeTooLong}
        </div>
      ) : dashboardError === "forbidden" ? (
        <AIBillingForbiddenBlock />
      ) : (
        <>
          <div className="row g-2 mb-3">
            <div className="col-6 col-xl-3">
              <AIBillingKpiCard
                label={translate.AIAvailableBalance}
                value={dashboard ? formatAmountUsd(dashboard.availableUsd) : ""}
                subtitle={translate.AICurrentCredit}
                loading={loading}
                error={dashboardError !== "none"}
                emphasis
              />
            </div>
            <div className="col-6 col-xl-3">
              <AIBillingKpiCard
                label={translate.AIConsumedThisMonth}
                value={dashboard ? formatAmountUsd(dashboard.consumedUsd) : ""}
                subtitle={translate.AIPeriodActivity}
                loading={loading}
                error={dashboardError !== "none"}
              />
            </div>
            <div className="col-6 col-xl-3">
              <AIBillingKpiCard
                label={translate.AITokensThisMonth}
                value={
                  dashboard
                    ? formatTokens(dashboard.tokensIn + dashboard.tokensOut)
                    : ""
                }
                subtitle={
                  dashboard
                    ? `${formatTokens(dashboard.tokensIn)} / ${formatTokens(dashboard.tokensOut)}`
                    : translate.AIInputOutput
                }
                loading={loading}
                error={dashboardError !== "none"}
              />
            </div>
            <div className="col-6 col-xl-3">
              <AIBillingKpiCard
                label={translate.Status}
                value={
                  dashboard ? (
                    <AIBillingStatusPill
                      label={getAgentStatusLabel(dashboard.status)}
                      variant={getAgentStatusVariant(dashboard.status)}
                      size="large"
                    />
                  ) : (
                    ""
                  )
                }
                subtitle={
                  dashboard ? getAgentStatusNote(dashboard.status) : undefined
                }
                loading={loading}
                error={dashboardError !== "none"}
              />
            </div>
          </div>

          <AIBillingStateBlock
            loading={loading}
            error={dashboardError}
            onRetry={retry}
            height={320}
          >
            {dashboard && (
              <>
                {showStatusAlert && (
                  <div
                    className={`alert alert-${statusAlert.variant} d-flex align-items-start gap-2`}
                    role="alert"
                  >
                    <i className="far fa-exclamation-triangle mt-1" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{statusAlert.label}</div>
                      <div style={{ fontSize: "0.8125rem" }}>
                        {statusAlert.note}
                      </div>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{ fontSize: "0.8125rem", fontWeight: 500 }}
                        onClick={() =>
                          navigateToAIBillingTab("AIAssistantConfiguration")
                        }
                      >
                        {translate.AIEditConfiguration}
                      </button>
                    </div>
                  </div>
                )}

                <div className="card mb-3" style={{ borderRadius: 10 }}>
                  <div className="card-body" style={{ padding: "14px 16px" }}>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                      <h6 className="mb-0" style={{ fontWeight: 500 }}>
                        {translate.AIPeriodUsage}
                      </h6>
                      <div className="d-flex align-content-center gap-3">
                        <a
                          href="#AIAgentUsageDetail"
                          className="align-content-center"
                        >
                          {translate.AIViewFullHistory}
                        </a>
                        {CompanyService.canDo("export") && (
                          <ButtonTooltipIcon
                            title={translate.Export}
                            icon="fa-light fa-arrow-down-to-line"
                            onClick={onExport}
                            isLink={false}
                            loading={exporting}
                            animation={false}
                          />
                        )}
                      </div>
                    </div>

                    {usageError !== "none" ? (
                      <p
                        className="text-danger mb-0"
                        style={{ fontSize: "0.8125rem" }}
                      >
                        {getErrorMessage(usageError)}
                      </p>
                    ) : latestUsage.length === 0 ? (
                      <p
                        className="text-muted mb-0"
                        style={{ fontSize: "0.8125rem" }}
                      >
                        {translate.AINoUsageYet}
                      </p>
                    ) : (
                      <AIBillingUsageTable items={latestUsage} />
                    )}
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-12 col-xl-6">
                    <div className="card h-100" style={{ borderRadius: 10 }}>
                      <div
                        className="card-body"
                        style={{ padding: "14px 16px" }}
                      >
                        <h6
                          className="mb-2 d-flex align-items-center gap-2"
                          style={{ fontWeight: 500 }}
                        >
                          <i className="far fa-credit-card" />
                          {translate.AILatestRecharges}
                        </h6>
                        {period && <AIBillingRechargeList range={period} />}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-6">
                    <div className="card h-100" style={{ borderRadius: 10 }}>
                      <div
                        className="card-body"
                        style={{ padding: "14px 16px" }}
                      >
                        <h6
                          className="mb-2 d-flex align-items-center gap-2"
                          style={{ fontWeight: 500 }}
                        >
                          <i className="far fa-bell" />
                          {translate.AIConfiguredAlert}
                        </h6>
                        <AIBillingAlertBox settings={MOCK_ALERT_SETTINGS} />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </AIBillingStateBlock>
        </>
      )}
    </div>
  );
};

export default AIAgentUsage;
